/**
 * Support contact channels shown by the floating support widget.
 *
 * Resolution order mirrors `lib/api.ts`:
 * 1. `window.APP_CONFIG.support` — runtime config from the Kubernetes ConfigMap
 * 2. `import.meta.env.VITE_SUPPORT_*` — build-time env (local development)
 * 3. undefined — the channel is simply not rendered
 *
 * Nothing here is required: an environment that configures no channel gets no widget.
 */

export const SUPPORT_CHANNEL_IDS = ["facebook", "zalo", "hotline"] as const;
export type SupportChannelId = (typeof SUPPORT_CHANNEL_IDS)[number];

export interface SupportChannel {
  id: SupportChannelId;
  /** Ready-to-use href: an https URL for chat channels, `tel:` for the hotline. */
  href: string;
  /** Second line under the channel name — the phone number for the hotline, empty otherwise. */
  detail?: string;
}

function runtime(): Partial<Record<SupportChannelId, string>> {
  return typeof window !== "undefined" ? (window.APP_CONFIG?.support ?? {}) : {};
}

/** Digits of a phone number, with every separator the operator typed removed. */
function toDigits(phone: string): string {
  return phone.replace(/[^\d]/g, "");
}

/**
 * `tel:` form of a phone number. Keeps a leading `+` so an international number
 * stays dialable — unlike a zalo.me path, which takes digits only.
 */
function toDialable(phone: string): string {
  const trimmed = phone.trim();
  return trimmed.startsWith("+") ? `+${toDigits(trimmed)}` : toDigits(trimmed);
}

/**
 * Returns the configured channels, in display order. Blank values are dropped so a
 * half-configured environment shows only what it actually has.
 */
export function getSupportChannels(): SupportChannel[] {
  const cfg = runtime();

  const facebook = (cfg.facebook ?? import.meta.env.VITE_SUPPORT_FACEBOOK_URL ?? "").trim();
  const zalo = (cfg.zalo ?? import.meta.env.VITE_SUPPORT_ZALO_URL ?? "").trim();
  const hotline = (cfg.hotline ?? import.meta.env.VITE_SUPPORT_HOTLINE ?? "").trim();

  const channels: SupportChannel[] = [];

  if (facebook) {
    channels.push({ id: "facebook", href: facebook });
  }

  if (zalo) {
    // A bare phone number is accepted as a shortcut for the zalo.me profile link.
    channels.push({
      id: "zalo",
      // Digits only: zalo.me rejects the `+` a tel: link would keep.
      href: /^https?:\/\//i.test(zalo) ? zalo : `https://zalo.me/${toDigits(zalo)}`,
    });
  }

  if (hotline) {
    channels.push({ id: "hotline", href: `tel:${toDialable(hotline)}`, detail: hotline });
  }

  return channels;
}
